import { useCallback, useMemo } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { FullView, FullViewContent } from '../../../../components/Modal'
import { ActionButton } from '../../../../components/Button'
import { ChevronBackward, Edit } from '@nine-thirty-five/material-symbols-react/rounded'
import {
  taskPageItems,
  useGetProject,
  useGetProjectTaskStats,
  useGetTasksPage,
} from '@zendia/frontend-shared'
import { axiosRequest } from '../../../../utilities/axiosClient'
import { ProjectView } from '../../../../components/Projects/ProjectView'
import { useLoadMoreOnScroll } from '../../../../hooks/useLoadMoreOnScroll'

export const Route = createFileRoute('/_authenticated/projects/$projectId/')({
  component: RouteComponent,
})

function RouteComponent() {
  const router = useRouter()
  const { projectId } = Route.useParams()
  const { data: projectResponse, isLoading, isError } = useGetProject(axiosRequest, projectId)
  // The project's tasks a page at a time, incomplete first, and its counts from the server
  const projectIds = useMemo(() => [projectId], [projectId])
  const {
    data: taskPages,
    isLoading: isLoadingTasks,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetTasksPage(axiosRequest, {
    sortBy: 'status',
    includeCompleted: true,
    labelIds: [],
    priorities: [],
    projectIds,
  })
  const { data: projectStats } = useGetProjectTaskStats(axiosRequest)
  const tasks = useMemo(() => taskPageItems(taskPages), [taskPages])
  const stats = projectStats?.find((row) => row.projectId === projectId)
  const loadMoreRef = useLoadMoreOnScroll({ hasNextPage, isFetchingNextPage, fetchNextPage })

  const onBack = useCallback(() => {
    router.navigate({ to: '/projects', replace: true })
  }, [router])

  const handleEdit = useCallback(() => {
    router.navigate({
      to: '/projects/$projectId/edit',
      params: { projectId },
    })
  }, [projectId, router])

  // const handleDelete = useCallback(() => {}, [])

  if (isLoading || isLoadingTasks) {
    return <div className="p-8 text-gray-500">Loading collection details...</div>
  }

  if (isError || !projectResponse?.data) {
    return <div className="p-8 text-red-500">Error loading collection details</div>
  }

  return (
    <FullView
      leftAction={
        <ActionButton
          onClick={onBack}
          label="Discard"
          icon={<ChevronBackward className="size-6" />}
        />
      }
      rightAction={
        <>
          <ActionButton
            onClick={handleEdit}
            label="Edit collection"
            icon={<Edit className="size-5" />}
          />
          {/* <ActionButton
            negative
            onClick={handleDelete}
            label="Delete collection"
            icon={<Delete className="size-5" />}
          /> */}
        </>
      }
    >
      <FullViewContent>
        <ProjectView project={projectResponse.data} tasks={tasks} stats={stats} />
        <div ref={loadMoreRef} aria-hidden="true" />
        {isFetchingNextPage && (
          <div className="text-center py-4 text-sm text-gray-500">Loading more tasks...</div>
        )}
      </FullViewContent>
    </FullView>
  )
}
